import { DateTime } from 'luxon'
import { Skill, skillFromDto, SkillJson } from '../../skills/models/skill'
import { Building, buildingFromDto } from '../../buildings/models/building'
import { Address, AddressDto, addressFromDto } from '../../addresses/models/address'
import type { Building as BuildingApiSchema } from '../../shared/api/generated/models/building'
import { IanaZone } from '../../shared/dates/types'
import z from 'zod'

export interface AreaDisplaySingleFieldConfig<T> {
    type: 'field'
    value: keyof T
}

export interface AreaDisplayExpressionFieldConfig {
    type: 'expression'
    value: string
}

export type AreaDisplayFieldConfig<T> = AreaDisplaySingleFieldConfig<T> | AreaDisplayExpressionFieldConfig

export const defaultDisplayNameConfig: AreaDisplayExpressionFieldConfig = {
    type: 'expression',
    value: '${building.customFields.business_unit} - ${name} - ${building.name}'
}

export const areaNestedValue = (obj: unknown, path: string): string | undefined => {
    if (typeof obj !== 'object' || obj === null) {
        return undefined
    }

    const keys = path.split('.')
    let currentValue: unknown = obj
    for (const key of keys) {
        if (!currentValue || typeof currentValue !== 'object' || !(key in currentValue)) {
            return undefined
        }

        currentValue = (currentValue as Record<string, unknown>)[key]
    }

    return typeof currentValue === 'string' ? currentValue : undefined
}

export const areaDisplayName = (area: Area, config: AreaDisplayFieldConfig<Area> = defaultDisplayNameConfig): string => {
    if (config.type === 'field') {
        return area[config.value] as string
    }

    if (config.type === 'expression') {
        return config.value.split(' - ')
            .map(part => {
                return part.replace(/\${([^}]+)}/g, (_, key) => {
                    const value = areaNestedValue(area, key)
                    return value !== undefined ? value : ''
                }).trim()
            })
            .filter(part => part !== '')
            .join(' - ')
    }

    return area.name
}

export const AreaID = z.string().brand<'AreaID'>()
export type AreaID = z.infer<typeof AreaID>

export interface AreaDto {
    id: string
    time_zone_id: string
    name: string
    color_hex: string
    archived_at: string | null

    actions?: string[]

    skills?: { data: SkillJson[] }
    building?: { data: BuildingApiSchema }
    address?: { data: AddressDto }
}

export interface Area {
    id: AreaID
    timeZoneId: IanaZone
    name: string
    colorHex: string
    archivedAt: DateTime | null

    actions?: string[]

    skills: readonly Skill[]

    building: Building | null

    address: Address | null
}

export const createArea = (
    params: {
        id: AreaID,
        timeZoneId?: string | null,
        name: string,
        colorHex: string,
        archivedAt: DateTime | null,

        actions?: string[],

        skills: Skill[],

        building: Building | null,

        address: Address | null
    }
): Area => ({
    id: params.id,
    timeZoneId: IanaZone.parse(params.timeZoneId),
    name: params.name,
    colorHex: params.colorHex,
    archivedAt: params.archivedAt,

    actions: params.actions,

    skills: params.skills,

    building: params.building,

    address: params.address,
})

export const areaFromDto = (dto: AreaDto): Area => (createArea({
    id: dto.id as AreaID,
    timeZoneId: dto.time_zone_id ?? null,
    name: dto.name,
    colorHex: dto.color_hex,
    archivedAt: dto.archived_at ? DateTime.fromISO(dto.archived_at) : null,

    actions: dto.actions,

    skills: dto.skills?.data?.map((skillJson) => skillFromDto(skillJson)) ?? [],

    building: dto.building?.data ? buildingFromDto(dto.building.data) : null,

    address: dto.address?.data ? addressFromDto(dto.address.data) : null,
}))
